import JsPDF from "jspdf";
import "jspdf-autotable";
import moment from "moment";
import { pdfHeader } from "@/utilities/pdfUtils";

/**
 * Generate PDF for cancellation list
 * @param {Object} options - PDF generation options
 * @param {Array} options.data - Filtered table data
 * @param {Object} options.settings - Application settings
 * @param {Object} options.filters - Applied filters
 * @returns {Object} - Success/error result
 */
export const generateCancellationListPdf = ({
  data,
  settings,
  filters = {},
  t,
  locale = "fr-FR",
}) => {
  try {
    const isArabic = locale === "ar-AR";
    const reverseIfArabic = (arr) => (isArabic ? arr.slice().reverse() : arr);

    // Build filter text for PDF header
    let filterText = "";
    const filterParts = [];

    if (filters.type)
      filterParts.push(`${t("tresorerie.type")}: ${filters.type}`);
    if (filters.clientName)
      filterParts.push(`${t("tresorerie.nomClient")}: ${filters.clientName}`);
    if (filters.search)
      filterParts.push(`${t("tresorerie.recherche")}: "${filters.search}"`);

    filterText = filterParts.join(" | ");

    // Initialize PDF document using pdfHeader utility
    const doc = pdfHeader(settings, {
      title: t("tresorerie.reglementsAnnules"),
      subTitle: filterText || null,
      locale: locale,
    });

    // Prepare table data
    const tableData = [];
    let totalAmount = 0;

    data.forEach((item) => {
      // Translate type value
      const translateType = (type) => {
        const typeMap = {
          Annulation: t("tresorerie.annulation"),
          Avoir: t("tresorerie.avoir"),
        };
        return typeMap[type] || type || "--";
      };

      const row = [
        item.serial || "--",
        item.clientName || "--",
        translateType(item.type),
        Array.isArray(item.allTypeReglement)
          ? item.allTypeReglement.join(", ")
          : "--",
        Number(item.money || 0).toFixed(2),
        item.date ? moment(item.date).format("DD/MM/YYYY") : "--",
        item.userName || "--",
      ];

      // Reverse for Arabic if needed
      tableData.push(reverseIfArabic(row));
      totalAmount += parseFloat(item.money || 0);
    });

    // Generate table
    doc.autoTable({
      theme: "grid",
      styles: {
        font: "Amiri",
        fontSize: 8,
        valign: "middle",
        halign: "center",
      },
      headStyles: {
        fillColor: [240, 240, 240],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        lineWidth: 0.1,
      },
      columnStyles: {
        0: { cellWidth: 20 }, // Serial
        1: { cellWidth: 35 }, // Client Name
        2: { cellWidth: 20 }, // Type
        3: { cellWidth: 35 }, // Type Reglement
        4: { cellWidth: 25, halign: "right" }, // Amount
        5: { cellWidth: 25 }, // Date
        6: { cellWidth: 25 }, // User
      },
      margin: { top: filterText ? 45 : 35 },
      head: [
        reverseIfArabic([
          t("tresorerie.numero"),
          t("tresorerie.nomParent"),
          t("tresorerie.type"),
          t("tresorerie.typesReglement"),
          t("tresorerie.montantTTC"),
          t("tresorerie.dateAnnulation"),
          t("tresorerie.annulePar"),
        ]),
      ],
      body: tableData,
      foot: [
        reverseIfArabic([
          {
            colSpan: 4,
            content: `${t("tresorerie.total")} :`,
            styles: {
              halign: "center",
              fontStyle: "bold",
              fillColor: [220, 220, 220],
            },
          },
          {
            content: `${totalAmount.toFixed(2)} TND`,
            styles: {
              halign: "right",
              fontStyle: "bold",
              fillColor: [220, 220, 220],
            },
          },
          {
            colSpan: 2,
            content: "",
            styles: { fillColor: [220, 220, 220] },
          },
        ]),
      ],
    });

    // Add summary statistics
    const finalY = doc.autoTableEndPosY() + 10;
    const pageWidth = doc.internal.pageSize.width;
    const statsX = isArabic ? pageWidth - 15 : 15;
    doc.setFontSize(10);
    doc.text(
      `${t("tresorerie.nombreTotalElements")}: ${data.length}`,
      statsX,
      finalY,
      {
        align: isArabic ? "right" : "left",
      }
    );

    // Count by type
    const typeCounts = data.reduce((acc, item) => {
      acc[item.type] = (acc[item.type] || 0) + 1;
      return acc;
    }, {});

    let yPosition = finalY + 5;
    Object.entries(typeCounts).forEach(([type, count]) => {
      // Translate type value
      const translateType = (type) => {
        const typeMap = {
          Annulation: t("tresorerie.annulation"),
          Avoir: t("tresorerie.avoir"),
        };
        return typeMap[type] || type;
      };
      doc.text(`${translateType(type)}: ${count}`, statsX, yPosition, {
        align: isArabic ? "right" : "left",
      });
      yPosition += 4;
    });

    // Add page numbering
    addPageNumbers(doc, t);

    // Generate filename with timestamp
    const timestamp = moment().format("YYYY-MM-DD_HH-mm");
    const filename = `${t("tresorerie.reglementsAnnules")}_${timestamp}.pdf`;

    // Save the PDF
    doc.save(filename);

    return {
      success: true,
      message: t("tresorerie.pdfGeneratedSuccessfully"),
      filename,
    };
  } catch (error) {
    console.error("Error generating PDF:", error);
    return {
      success: false,
      message: t("tresorerie.pdfGenerationError"),
      error: error.message,
    };
  }
};

/**
 * Generate PDF for a specific cancellation detail
 * @param {Object} cancellation - Single cancellation data
 * @param {Object} settings - Application settings
 * @returns {Object} - Success/error result
 */
export const generateSingleCancellationPdf = (
  cancellation,
  settings,
  t,
  locale = "fr-FR"
) => {
  try {
    const isArabic = locale === "ar-AR";
    const reverseIfArabic = (arr) => (isArabic ? arr.slice().reverse() : arr);

    // Translate type for title and details
    const translateType = (type) => {
      const typeMap = {
        Annulation: t("tresorerie.annulation"),
        Avoir: t("tresorerie.avoir"),
      };
      return typeMap[type] || type;
    };

    // Initialize PDF document using pdfHeader utility
    const doc = pdfHeader(settings, {
      title: `${t("tresorerie.detail")} ${translateType(cancellation.type)} - ${
        cancellation.serial
      }`,
      locale: locale,
    });

    // Cancellation details
    const details = [
      reverseIfArabic([t("tresorerie.numSerie"), cancellation.serial || "--"]),
      reverseIfArabic([
        t("tresorerie.nomClient"),
        cancellation.clientName || "--",
      ]),
      reverseIfArabic([
        t("tresorerie.type"),
        translateType(cancellation.type) || "--",
      ]),
      reverseIfArabic([
        t("tresorerie.montant"),
        `${Number(cancellation.money || 0).toFixed(2)} TND`,
      ]),
      reverseIfArabic([
        t("tresorerie.dateAnnulation"),
        cancellation.date
          ? moment(cancellation.date).format("DD/MM/YYYY")
          : "--",
      ]),
      reverseIfArabic([
        t("tresorerie.annulePar"),
        cancellation.userName || "--",
      ]),
      reverseIfArabic([
        t("tresorerie.facture"),
        cancellation.bill ? t("tresorerie.oui") : t("tresorerie.non"),
      ]),
    ];

    // Add payment types if available
    if (
      cancellation.allTypeReglement &&
      Array.isArray(cancellation.allTypeReglement)
    ) {
      details.push(
        reverseIfArabic([
          t("tresorerie.typesReglement"),
          cancellation.allTypeReglement.join(", "),
        ])
      );
    }

    // Add note if available
    if (cancellation.note && cancellation.note !== "--") {
      details.push(reverseIfArabic([t("tresorerie.note"), cancellation.note]));
    }

    doc.autoTable({
      theme: "grid",
      styles: {
        font: "Amiri",
        valign: "middle",
        fontSize: 11,
        cellPadding: 5,
      },
      columnStyles: {
        0: { cellWidth: 60, fontStyle: "bold", fillColor: [240, 240, 240] },
        1: { cellWidth: 120 },
      },
      body: details,
      startY: 35,
    });

    const filename = `${cancellation.type}_${
      cancellation.serial
    }_${moment().format("YYYY-MM-DD")}.pdf`;
    doc.save(filename);

    return {
      success: true,
      message: t("tresorerie.pdfGeneratedSuccessfully"),
      filename,
    };
  } catch (error) {
    console.error("Error generating single cancellation PDF:", error);
    return {
      success: false,
      message: t("tresorerie.pdfGenerationError"),
      error: error.message,
    };
  }
};

/**
 * Add page numbers to the PDF document
 * @param {Object} doc - jsPDF document instance
 * @param {Function} t - Translation function (i18n.t)
 */
const addPageNumbers = (doc, t) => {
  const pages = doc.internal.getNumberOfPages();
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;

  doc.setFontSize(8);

  for (let j = 1; j <= pages; j++) {
    const horizontalPos = pageWidth / 2;
    const verticalPos = pageHeight - 10;
    doc.setPage(j);
    const pageText = t("pdf.pageNumber", {
      current: j,
      total: pages,
    });
    doc.text(pageText, horizontalPos, verticalPos, {
      align: "center",
    });
  }
};
